import { useEffect, useState } from 'react';
import { useSchemaOrg, getLocalBusinessSchema, getWebsiteSchema, getOrganizationSchema, getOfferSchema } from '@/utils/seo';

export default function HomePage() {
  // Add Schema.org structured data
  useSchemaOrg([
    getLocalBusinessSchema(),
    getWebsiteSchema(),
    getOrganizationSchema(),
    getOfferSchema()
  ]);

  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMobileMenuOpen(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <h1>
                <img
                  src="https://static.readdy.ai/image/4576fe993686e3ff995f20cc75a6e973/173fc1455cc9648e7b8be445154b6b64.png"
                  alt="PG PW Malda Logo - Physics Wallah Boys Hostel"
                  className="h-12 w-auto"
                />
              </h1>
            </div>
            <div className="hidden md:flex items-center gap-8">
              <button
                onClick={() => scrollToSection('home')}
                className="text-gray-900 hover:text-orange-500 transition-colors whitespace-nowrap cursor-pointer"
              >
                Home
              </button>
              <button
                onClick={() => scrollToSection('about')}
                className="text-gray-900 hover:text-orange-500 transition-colors whitespace-nowrap cursor-pointer"
              >
                About
              </button>
              <button
                onClick={() => scrollToSection('amenities')}
                className="text-gray-900 hover:text-orange-500 transition-colors whitespace-nowrap cursor-pointer"
              >
                Services
              </button>
              <button
                onClick={() => scrollToSection('rooms')}
                className="text-gray-900 hover:text-orange-500 transition-colors whitespace-nowrap cursor-pointer"
              >
                Gallery
              </button>
              <button
                onClick={() => scrollToSection('contact')}
                className="text-gray-900 hover:text-orange-500 transition-colors whitespace-nowrap cursor-pointer"
              >
                Contact
              </button>
            </div>
            <a
              href="tel:+917479023201"
              className="flex items-center gap-2 font-semibold text-gray-900 hover:text-orange-500 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-fill text-orange-500"></i>
              <span className="hidden sm:inline">+91 7479023201</span>
            </a>
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden cursor-pointer text-gray-900 ml-4"
            >
              <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-200">
            <div className="px-6 py-4 space-y-3">
              <button
                onClick={() => scrollToSection('home')}
                className="block w-full text-left text-gray-700 hover:text-orange-500 transition-colors py-2"
              >
                Home
              </button>
              <button
                onClick={() => scrollToSection('about')}
                className="block w-full text-left text-gray-700 hover:text-orange-500 transition-colors py-2"
              >
                About
              </button>
              <button
                onClick={() => scrollToSection('amenities')}
                className="block w-full text-left text-gray-700 hover:text-orange-500 transition-colors py-2"
              >
                Services
              </button>
              <button
                onClick={() => scrollToSection('rooms')}
                className="block w-full text-left text-gray-700 hover:text-orange-500 transition-colors py-2"
              >
                Gallery
              </button>
              <button
                onClick={() => scrollToSection('contact')}
                className="block w-full text-left text-gray-700 hover:text-orange-500 transition-colors py-2"
              >
                Contact
              </button>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center justify-center pt-20">
        <div className="absolute inset-0 z-0">
          <img
            src="https://static.readdy.ai/image/4b14e1bb1a7b82bff99828a132017b99/e7209b0a7b0292654e5adb3d65078f42.jpeg"
            alt="PW PG Malda Boys Hostel Physics Wallah NEET"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-black/60"></div>
        </div>
        <div className="relative z-10 max-w-5xl mx-auto px-6 text-center w-full">
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-4 leading-tight">
            Affordable PG Boys Hostel
          </h1>
          <p className="text-3xl md:text-4xl text-white/90 mb-3 font-medium">in Malda Town</p>
          <p className="text-xl md:text-2xl text-orange-400 mb-8 font-semibold">
            Perfect for Physics Wallah Students, NEET Aspirants & Working Professionals
          </p>
          <p className="text-lg md:text-xl text-white/90 mb-10 max-w-3xl mx-auto">
            3 Times Homely Unlimited Food • High Speed WiFi • 24x7 Facilities
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <button
              onClick={() => scrollToSection('contact')}
              className="px-8 py-4 bg-orange-500 text-white rounded-full font-semibold text-lg hover:bg-orange-600 transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
              Book Your Room
            </button>
            <button
              onClick={() => scrollToSection('rooms')}
              className="px-8 py-4 bg-white text-orange-500 rounded-full font-semibold text-lg hover:bg-gray-50 transition-all border-2 border-white whitespace-nowrap cursor-pointer"
            >
              View Gallery
            </button>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
                About PW PG Malda
              </h2>
              <p className="text-lg text-gray-700 mb-8 leading-relaxed">
                <strong>PW PG Malda</strong> is strategically located on <strong>Malda Town Station Road</strong>, offering easy access to coaching centers and workplaces. Our <strong>boys hostel in Malda</strong> provides a perfect blend of comfort, convenience, and community for <strong>Physics Wallah students</strong>, <strong>NEET aspirants</strong>, and working professionals.
              </p>
              <p className="text-lg text-gray-700 mb-8 leading-relaxed">
                We understand the needs of <strong>PW students</strong> and <strong>NEET students in Malda</strong>, which is why we've created a space that feels like home. With modern amenities, nutritious <strong>mess food</strong>, and a supportive environment, we ensure you can focus on your goals without any worries. Our <strong>Malda mess</strong> serves unlimited homely food three times daily.
              </p>
              <a
                href="https://www.google.com/maps/search/?api=1&query=Malda+Town+Station+Road"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 bg-green-100 text-green-800 rounded-full font-semibold hover:bg-green-200 transition-colors mb-10 whitespace-nowrap cursor-pointer"
              >
                Get Directions
                <i className="ri-arrow-right-line"></i>
              </a>
              <div className="grid grid-cols-2 gap-8">
                <div>
                  <div className="text-5xl font-bold text-gray-900 mb-2">50+</div>
                  <div className="text-sm text-gray-600">Happy Residents</div>
                </div>
                <div>
                  <div className="text-5xl font-bold text-gray-900 mb-2">4.8★</div>
                  <div className="text-sm text-gray-600">Average Rating</div>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src="https://static.readdy.ai/image/4576fe993686e3ff995f20cc75a6e973/0b9a779ace1aefe9d812c1accb7e6992.jpeg"
                  alt="PW PG Malda Location Boys Hostel"
                  className="w-full h-full object-contain bg-gray-100"
                />
                <div className="absolute top-6 left-6">
                  <div className="bg-white px-4 py-2 rounded-full shadow-lg flex items-center gap-2 whitespace-nowrap">
                    <i className="ri-map-pin-fill text-orange-500"></i>
                    <span className="font-semibold text-gray-900">Malda Town Station Road</span>
                  </div>
                </div>
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-6">
                  <div className="text-white">
                    <div className="text-2xl font-bold mb-1">Prime Location</div>
                    <div className="text-white/90">Easy Access to Everywhere</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services/Amenities Section */}
      <section id="amenities" className="py-20 bg-gradient-to-b from-orange-50/30 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-16">
            <div className="text-orange-500 font-semibold mb-3 text-sm tracking-wider uppercase">
              WHY CHOOSE US
            </div>
            <h2 className="text-4xl md:text-6xl font-bold text-gray-900 mb-4 leading-tight">
              World-Class Amenities
            </h2>
            <p className="text-xl text-gray-600">Everything you need for comfortable living in Malda's best boys hostel</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-gradient-to-br from-green-100 to-green-50 rounded-3xl p-8 hover:shadow-xl transition-all">
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mb-6 mx-auto">
                <i className="ri-restaurant-2-fill text-green-600 text-3xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3 text-center">Unlimited Mess Food</h3>
              <p className="text-gray-700 text-center">
                3 times homely meals daily with unlimited servings at our Malda mess
              </p>
            </div>
            <div className="bg-gradient-to-br from-blue-100 to-blue-50 rounded-3xl p-8 hover:shadow-xl transition-all">
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mb-6 mx-auto">
                <i className="ri-wifi-fill text-blue-600 text-3xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3 text-center">High Speed WiFi</h3>
              <p className="text-gray-700 text-center">
                Uninterrupted high-speed internet for PW and NEET online classes
              </p>
            </div>
            <div className="bg-gradient-to-br from-purple-100 to-purple-50 rounded-3xl p-8 hover:shadow-xl transition-all">
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mb-6 mx-auto">
                <i className="ri-hotel-bed-fill text-purple-600 text-3xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3 text-center">New Rooms</h3>
              <p className="text-gray-700 text-center">Modern rooms with brand new bathrooms</p>
            </div>
            <div className="bg-gradient-to-br from-orange-100 to-orange-50 rounded-3xl p-8 hover:shadow-xl transition-all">
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mb-6 mx-auto">
                <i className="ri-flashlight-fill text-orange-600 text-3xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3 text-center">24x7 Services</h3>
              <p className="text-gray-700 text-center">
                Round-the-clock electricity and water supply
              </p>
            </div>
          </div>
          <div className="mt-16 grid md:grid-cols-3 gap-6">
            <div className="bg-white rounded-2xl p-6 shadow-md hover:shadow-lg transition-all border border-gray-100">
              <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mb-4">
                <i className="ri-shield-check-fill text-orange-600 text-2xl"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-2">Safe & Secure</h4>
              <p className="text-gray-600">CCTV surveillance and secure entry system for boys hostel</p>
            </div>
            <div className="bg-white rounded-2xl p-6 shadow-md hover:shadow-lg transition-all border border-gray-100">
              <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mb-4">
                <i className="ri-team-fill text-orange-600 text-2xl"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-2">Study Environment</h4>
              <p className="text-gray-600">Quiet atmosphere perfect for Physics Wallah and NEET preparation</p>
            </div>
            <div className="bg-white rounded-2xl p-6 shadow-md hover:shadow-lg transition-all border border-gray-100">
              <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mb-4">
                <i className="ri-customer-service-2-fill text-orange-600 text-2xl"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-2">24x7 Support</h4>
              <p className="text-gray-600">Always available to help with any concerns</p>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery/Rooms Section */}
      <section id="rooms" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-2">
              Explore Our <span className="italic font-serif">Rooms & Spaces</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="relative rounded-3xl overflow-hidden shadow-xl group">
              <div className="w-full h-[400px] md:h-[500px]">
                <img
                  src="https://static.readdy.ai/image/4576fe993686e3ff995f20cc75a6e973/c52738a43977eab6ee8d8372e4e74bb0.jpeg"
                  alt="Premium Room"
                  className="w-full h-full object-contain bg-gray-100 group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="absolute top-6 left-6">
                <div className="bg-white px-4 py-2 rounded-full shadow-lg font-semibold text-gray-900 whitespace-nowrap">
                  NEW ROOMS
                </div>
              </div>
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-6">
                <div className="text-white text-2xl font-bold">Modern & Spacious</div>
              </div>
            </div>
            <div className="relative rounded-3xl overflow-hidden shadow-xl group">
              <div className="w-full h-[400px] md:h-[500px]">
                <img
                  src="https://static.readdy.ai/image/4b14e1bb1a7b82bff99828a132017b99/e7209b0a7b0292654e5adb3d65078f42.jpeg"
                  alt="Hostel Room"
                  className="w-full h-full object-contain bg-gray-100 group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-6">
                <div className="text-white text-2xl font-bold">Hostel Room</div>
              </div>
            </div>
            <div className="relative rounded-3xl overflow-hidden shadow-xl group">
              <div className="w-full h-[300px]">
                <img
                  src="https://static.readdy.ai/image/4576fe993686e3ff995f20cc75a6e973/ea210ada3ad8494cc95ce88eccd14f9e.jpeg"
                  alt="Modern Bathroom"
                  className="w-full h-full object-contain bg-gray-100 group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-4">
                <div className="text-white font-semibold">New Bathrooms</div>
              </div>
            </div>
            <div className="relative rounded-3xl overflow-hidden shadow-xl group">
              <div className="w-full h-[300px]">
                <img
                  src="https://static.readdy.ai/image/4576fe993686e3ff995f20cc75a6e973/1daa990b8783a9a0c3a6f14a2f155944.jpeg"
                  alt="Common Area"
                  className="w-full h-full object-contain bg-gray-100 group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-4">
                <div className="text-white font-semibold">Common Area</div>
              </div>
            </div>
            <div className="relative rounded-3xl overflow-hidden shadow-xl group md:col-span-2">
              <div className="w-full h-[300px]">
                <img
                  src="https://static.readdy.ai/image/4576fe993686e3ff995f20cc75a6e973/78d7bca128d42a236e2912affaf43d88.jpeg"
                  alt="Toilets"
                  className="w-full h-full object-contain bg-gray-100 group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-4">
                <div className="text-white font-semibold">Toilets</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 bg-gradient-to-b from-white to-orange-50/30">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-0 rounded-3xl overflow-hidden shadow-2xl">
            <div className="relative bg-orange-100 p-12 flex items-center justify-center min-h-[500px]">
              <img
                src="https://static.readdy.ai/image/4576fe993686e3ff995f20cc75a6e973/4b70f7b618d6a614dbe73ac62b64ed59.jpeg"
                alt="Happy Physics Wallah and NEET Students"
                className="absolute inset-0 w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-br from-orange-500/40 to-orange-600/40"></div>
              <div className="relative z-10">
                <div className="bg-black/70 px-4 py-2 rounded-full inline-block mb-6 whitespace-nowrap">
                  <span className="text-white font-semibold text-sm">AFFORDABLE PRICING</span>
                </div>
                <div className="text-white">
                  <div className="text-2xl font-medium mb-2">Starting</div>
                  <div className="text-7xl md:text-8xl font-bold mb-2">₹6000</div>
                  <div className="text-xl">PER MONTH</div>
                </div>
              </div>
            </div>
            <div className="bg-white p-12 flex flex-col justify-center">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
                Everything<br />Included
              </h2>
              <div className="space-y-4 mb-8">
                <p className="text-lg text-gray-700">No hidden charges. No extra fees.</p>
                <p className="text-lg text-gray-700">
                  Room rent includes unlimited mess food, WiFi, electricity, water, and all amenities.
                </p>
                <p className="text-lg text-gray-700">
                  Best value for <strong>Physics Wallah students</strong>, <strong>NEET aspirants</strong>, and working professionals in <strong>Malda</strong>.
                </p>
              </div>
              <div className="space-y-3 mb-8">
                <div className="flex items-center gap-3">
                  <i className="ri-check-line text-green-600 text-xl"></i>
                  <span className="text-gray-800">3 Times Unlimited Homely Mess Food</span>
                </div>
                <div className="flex items-center gap-3">
                  <i className="ri-check-line text-green-600 text-xl"></i>
                  <span className="text-gray-800">High Speed WiFi Internet</span>
                </div>
                <div className="flex items-center gap-3">
                  <i className="ri-check-line text-green-600 text-xl"></i>
                  <span className="text-gray-800">24x7 Electricity & Water</span>
                </div>
                <div className="flex items-center gap-3">
                  <i className="ri-check-line text-green-600 text-xl"></i>
                  <span className="text-gray-800">New Rooms & Bathrooms</span>
                </div>
                <div className="flex items-center gap-3">
                  <i className="ri-check-line text-green-600 text-xl"></i>
                  <span className="text-gray-800">Safe & Secure Environment</span>
                </div>
              </div>
              <button
                onClick={() => scrollToSection('contact')}
                className="px-8 py-4 bg-gray-900 text-white rounded-full font-semibold text-lg hover:bg-gray-800 transition-all flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
              >
                Contact Us Now
                <i className="ri-arrow-right-line"></i>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-gradient-to-b from-orange-50/30 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-16">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
              <span className="text-orange-500 font-semibold text-sm tracking-wider uppercase">
                STUDENT REVIEWS
              </span>
            </div>
            <h2 className="text-4xl md:text-6xl font-bold text-gray-900 mb-4 leading-tight">
              What Our Residents<br />Say About Us
            </h2>
            <p className="text-xl text-gray-600">Real experiences from Physics Wallah and NEET students</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white rounded-2xl p-6 shadow-md hover:shadow-xl transition-all">
              <div className="flex gap-1 mb-4">
                <i className="ri-star-fill text-orange-500"></i>
                <i className="ri-star-fill text-orange-500"></i>
                <i className="ri-star-fill text-orange-500"></i>
                <i className="ri-star-fill text-orange-500"></i>
                <i className="ri-star-fill text-orange-500"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-3">Best PG in Malda</h4>
              <p className="text-gray-600 mb-4">
                The mess food quality is amazing! Feels just like home-cooked meals. The unlimited servings are a huge plus for students like us.
              </p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-orange-400 to-orange-600 rounded-full flex items-center justify-center text-white font-bold">
                  R
                </div>
                <div>
                  <div className="font-bold text-gray-900">Rahul Kumar</div>
                  <div className="text-sm text-gray-500">Physics Wallah Student</div>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-2xl p-6 shadow-md hover:shadow-xl transition-all">
              <div className="flex gap-1 mb-4">
                <i className="ri-star-fill text-orange-500"></i>
                <i className="ri-star-fill text-orange-500"></i>
                <i className="ri-star-fill text-orange-500"></i>
                <i className="ri-star-fill text-orange-500"></i>
                <i className="ri-star-fill text-orange-500"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-3">Perfect for NEET Studies</h4>
              <p className="text-gray-600 mb-4">
                High-speed WiFi works flawlessly. I can attend online classes and download study materials without any issues. Great for NEET preparation!
              </p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full flex items-center justify-center text-white font-bold">
                  A
                </div>
                <div>
                  <div className="font-bold text-gray-900">Amit Singh</div>
                  <div className="text-sm text-gray-500">NEET Aspirant</div>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-2xl p-6 shadow-md hover:shadow-xl transition-all">
              <div className="flex gap-1 mb-4">
                <i className="ri-star-fill text-orange-500"></i>
                <i className="ri-star-fill text-orange-500"></i>
                <i className="ri-star-fill text-orange-500"></i>
                <i className="ri-star-fill text-orange-500"></i>
                <i className="ri-star-fill text-orange-500"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-3">Clean & Comfortable</h4>
              <p className="text-gray-600 mb-4">
                The rooms and bathrooms are brand new and very well maintained. Everything is clean and the staff is very helpful.
              </p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center text-white font-bold">
                  S
                </div>
                <div>
                  <div className="font-bold text-gray-900">Sanjay Sharma</div>
                  <div className="text-sm text-gray-500">Working Professional</div>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-2xl p-6 shadow-md hover:shadow-xl transition-all">
              <div className="flex gap-1 mb-4">
                <i className="ri-star-fill text-orange-500"></i>
                <i className="ri-star-fill text-orange-500"></i>
                <i className="ri-star-fill text-orange-500"></i>
                <i className="ri-star-fill text-orange-500"></i>
                <i className="ri-star-fill text-orange-500"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-3">Great Location</h4>
              <p className="text-gray-600 mb-4">
                Located right on Station Road, very easy to reach coaching centers. The location is perfect for Physics Wallah students.
              </p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-purple-400 to-purple-600 rounded-full flex items-center justify-center text-white font-bold">
                  V
                </div>
                <div>
                  <div className="font-bold text-gray-900">Vikash Gupta</div>
                  <div className="text-sm text-gray-500">PW Student</div>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-2xl p-6 shadow-md hover:shadow-xl transition-all">
              <div className="flex gap-1 mb-4">
                <i className="ri-star-fill text-orange-500"></i>
                <i className="ri-star-fill text-orange-500"></i>
                <i className="ri-star-fill text-orange-500"></i>
                <i className="ri-star-fill text-orange-500"></i>
                <i className="ri-star-fill text-orange-500"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-3">Value for Money</h4>
              <p className="text-gray-600 mb-4">
                At ₹6000 with everything included, this is the best deal in Malda. No hidden charges, everything transparent.
              </p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-red-400 to-red-600 rounded-full flex items-center justify-center text-white font-bold">
                  P
                </div>
                <div>
                  <div className="font-bold text-gray-900">Pradeep Yadav</div>
                  <div className="text-sm text-gray-500">NEET Student</div>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-2xl p-6 shadow-md hover:shadow-xl transition-all">
              <div className="flex gap-1 mb-4">
                <i className="ri-star-fill text-orange-500"></i>
                <i className="ri-star-fill text-orange-500"></i>
                <i className="ri-star-fill text-orange-500"></i>
                <i className="ri-star-fill text-orange-500"></i>
                <i className="ri-star-fill text-orange-500"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-3">Highly Recommended</h4>
              <p className="text-gray-600 mb-4">
                Safe, secure, and peaceful environment. Perfect for focused NEET preparation. I highly recommend PW PG Malda to all students.
              </p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-full flex items-center justify-center text-white font-bold">
                  M
                </div>
                <div>
                  <div className="font-bold text-gray-900">Manish Jha</div>
                  <div className="text-sm text-gray-500">Physics Wallah Student</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-6xl font-bold text-gray-900 mb-4">Get In Touch</h2>
            <p className="text-xl text-gray-600">We're here to answer all your questions</p>
          </div>
          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Contact Information</h3>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-phone-fill text-orange-600 text-xl"></i>
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900 mb-1">Phone</div>
                    <a
                      href="tel:+917479023201"
                      className="text-lg text-gray-700 hover:text-orange-500 transition-colors cursor-pointer"
                    >
                      +91 7479023201
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-fill text-orange-600 text-xl"></i>
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900 mb-1">Address</div>
                    <p className="text-lg text-gray-700">
                      Malda Town Station Road
                      <br />
                      Malda, West Bengal
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-time-fill text-orange-600 text-xl"></i>
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900 mb-1">Visit Us</div>
                    <p className="text-lg text-gray-700">
                      Monday - Sunday
                      <br />
                      9:00 AM - 8:00 PM
                    </p>
                  </div>
                </div>
              </div>
              <div className="mt-8">
                <a
                  href="https://wa.me/917479023201"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-green-500 text-white rounded-full font-semibold hover:bg-green-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-whatsapp-fill text-xl"></i>
                  Chat on WhatsApp
                </a>
              </div>
            </div>
            <div className="bg-gray-50 rounded-2xl p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Send us a Message</h3>
              <form className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Your Name
                  </label>
                  <input
                    type="text"
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-orange-500 focus:ring-2 focus:ring-orange-200 outline-none transition-all text-sm"
                    placeholder="Enter your name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-orange-500 focus:ring-2 focus:ring-orange-200 outline-none transition-all text-sm"
                    placeholder="Enter your phone number"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Message
                  </label>
                  <textarea
                    rows={4}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-orange-500 focus:ring-2 focus:ring-orange-200 outline-none transition-all resize-none text-sm"
                    placeholder="Tell us about your requirements"
                  ></textarea>
                </div>
                <button
                  type="submit"
                  className="w-full px-6 py-3 bg-orange-500 text-white rounded-lg font-semibold hover:bg-orange-600 transition-all whitespace-nowrap cursor-pointer"
                >
                  Send Message
                </button>
              </form>
            </div>
          </div>
          <div className="mt-12 rounded-2xl overflow-hidden shadow-xl h-96">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3623.123456789!2d88.14!3d25.01!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjXCsDAwJzM2LjAiTiA4OMKwMDgnMjQuMCJF!5e0!3m2!1sen!2sin!4v1234567890"
              width="100%"
              height="100%"
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="PW PG Malda Location"
            ></iframe>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16 mt-20 mx-6 rounded-3xl">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-12 gap-12 mb-12">
            <div className="md:col-span-6">
              <h3 className="text-4xl font-light mb-4">Stay Connected</h3>
              <p className="text-gray-400 mb-8">Get updates about availability and offers</p>
              <form className="max-w-md">
                <div className="border-b border-gray-600 pb-2 mb-4">
                  <input
                    type="email"
                    className="w-full bg-transparent text-white placeholder-gray-500 outline-none text-sm"
                    placeholder="Enter your email"
                    required
                  />
                </div>
                <button
                  type="submit"
                  className="px-6 py-3 bg-white text-gray-900 rounded-full font-semibold hover:bg-gray-100 transition-all flex items-center gap-2 whitespace-nowrap cursor-pointer"
                >
                  Subscribe
                  <i className="ri-arrow-right-line"></i>
                </button>
              </form>
            </div>
            <div className="md:col-span-3">
              <h4 className="text-white font-semibold mb-4">Quick Links</h4>
              <div className="space-y-3">
                <button
                  onClick={() => scrollToSection('home')}
                  className="block text-gray-400 hover:text-white transition-colors cursor-pointer text-left"
                >
                  Home
                </button>
                <button
                  onClick={() => scrollToSection('about')}
                  className="block text-gray-400 hover:text-white transition-colors cursor-pointer text-left"
                >
                  About
                </button>
                <button
                  onClick={() => scrollToSection('amenities')}
                  className="block text-gray-400 hover:text-white transition-colors cursor-pointer text-left"
                >
                  Amenities
                </button>
                <button
                  onClick={() => scrollToSection('rooms')}
                  className="block text-gray-400 hover:text-white transition-colors cursor-pointer text-left"
                >
                  Rooms
                </button>
                <button
                  onClick={() => scrollToSection('contact')}
                  className="block text-gray-400 hover:text-white transition-colors cursor-pointer text-left"
                >
                  Contact
                </button>
              </div>
            </div>
            <div className="md:col-span-3">
              <h4 className="text-white font-semibold mb-4">Contact Us</h4>
              <div className="space-y-3 text-gray-400">
                <a
                  href="tel:+917479023201"
                  className="block hover:text-white transition-colors cursor-pointer"
                >
                  +91 7479023201
                </a>
                <a
                  href="mailto:info@pwpgmalda.com"
                  className="block hover:text-white transition-colors cursor-pointer"
                >
                  info@pwpgmalda.com
                </a>
              </div>
              <h4 className="text-white font-semibold mb-4 mt-6">Address</h4>
              <p className="text-gray-400">
                Malda Town Station Road
                <br />
                Malda, West Bengal
              </p>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <div className="flex gap-6">
                <a href="#" className="text-gray-400 hover:text-white transition-colors cursor-pointer">
                  Facebook
                </a>
                <a href="#" className="text-gray-400 hover:text-white transition-colors cursor-pointer">
                  Instagram
                </a>
                <a
                  href="https://wa.me/917479023201"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-white transition-colors cursor-pointer"
                >
                  WhatsApp
                </a>
              </div>
              <div className="text-gray-400 text-sm text-center">
                © 2025 PW PG Malda. All rights reserved.
              </div>
              <div className="flex gap-6">
                <a href="#" className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">
                  Privacy Policy
                </a>
                <a href="#" className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">
                  Terms
                </a>
              </div>
            </div>
            <div className="text-center mt-6">
              <a
                href="https://readdy.ai/?ref=logo"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-500 hover:text-gray-400 transition-colors text-sm cursor-pointer"
              >
                Powered by Readdy
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
